// @gs/feedback/client — PublishAnnouncementModal : le personnel d une application publie une
// nouveaute au tableau des commentaires (feat-feedback-les-nouveautes-se-publient, mig 293 ;
// prototype ZoneSecur components/feedback/PublishFeaturePostModal.tsx). Titre, description,
// visibilite publique (toute l equipe de l application) ou ciblee (des personnes invitees, que le
// serveur propose et refiltre), pieces jointes par le proxy existant (POST /:id/attachments).
// DS V2, FR et EN (namespace feedback).
import { useEffect, useRef, useState } from 'react';
import { Check, Globe, Lock, Paperclip, Search, X } from 'lucide-react';
import { useTranslation } from '@gs/i18n';
import {
  TITLE_MAX,
  BODY_MAX,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  ATTACHMENT_CONTENT_TYPES,
  ANNOUNCEMENT_TITLE_MIN,
  ANNOUNCEMENT_DESCRIPTION_MIN,
  ANNOUNCEMENT_INVITEES_MAX,
  type AnnouncementVisibility,
} from '../schemas';
import { usePublishAnnouncement, uploadAttachment } from './hooks';
import { useInvitables } from './invitablesQuery';
import { BTN_PRIMARY, BTN_SECONDARY, INPUT_BASE } from './chipTokens';

export interface PublishAnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  /** Appelee apres publication reussie (id + numero) — ex: ouvrir le fil. */
  onPublished?: (ticket: { id: string; ticketNumber: string }) => void;
}

/** Delai avant de chercher : une requete par pause de frappe, pas une par touche. */
const RECHERCHE_DELAI_MS = 250;

export function PublishAnnouncementModal({ open, onClose, onPublished }: PublishAnnouncementModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<AnnouncementVisibility>('PUBLIC');
  const [invites, setInvites] = useState<Array<{ id: string; name: string }>>([]);
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const publish = usePublishAnnouncement();
  const ciblee = visibility === 'TARGETED';
  const invitables = useInvitables(recherche, open && ciblee);

  useEffect(() => {
    const h = setTimeout(() => setRecherche(saisie.trim()), RECHERCHE_DELAI_MS);
    return () => clearTimeout(h);
  }, [saisie]);

  if (!open) return null;

  const canSubmit =
    !submitting
    && title.trim().length >= ANNOUNCEMENT_TITLE_MIN
    && description.trim().length >= ANNOUNCEMENT_DESCRIPTION_MIN
    && (!ciblee || invites.length > 0);

  const toggleInvite = (p: { id: string; name: string }) => {
    setInvites((prev) => prev.some((x) => x.id === p.id)
      ? prev.filter((x) => x.id !== p.id)
      : prev.length >= ANNOUNCEMENT_INVITEES_MAX ? prev : [...prev, p]);
  };

  const onFilesPicked = (picked: FileList | null) => {
    if (!picked) return;
    setError(null);
    const next = [...files];
    for (const f of Array.from(picked)) {
      if (!ATTACHMENT_CONTENT_TYPES.test(f.type)) { setError(t('feedback:modal.attachmentTypeError')); return; }
      if (f.size > MAX_ATTACHMENT_BYTES) { setError(t('feedback:modal.attachmentSizeError')); return; }
      if (next.length >= MAX_ATTACHMENTS) { setError(t('feedback:modal.attachmentCountError', { max: MAX_ATTACHMENTS })); return; }
      next.push(f);
    }
    setFiles(next);
  };

  const reset = () => {
    setTitle(''); setDescription(''); setVisibility('PUBLIC'); setInvites([]);
    setSaisie(''); setRecherche(''); setFiles([]); setError(null);
  };

  const close = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await publish.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        visibility,
        invitees: ciblee ? invites.map((p) => p.id) : undefined,
      });
      // PJ apres publication (proxy existant, POST /:id/attachments) — echec PJ non bloquant.
      for (const f of files) {
        await uploadAttachment(created.id, f, 'ticket').catch(() => { /* fail-soft PJ */ });
      }
      reset();
      onClose();
      onPublished?.(created);
    } catch (err) {
      const code = (err as { body?: { code?: unknown } } | null)?.body?.code;
      setError(code === 'INVITES_INVALIDES' ? t('feedback:publish.inviteesError') : t('feedback:publish.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const proposees = invitables.data ?? [];
  const choixVisibilite: Array<{ v: AnnouncementVisibility; label: string; hint: string; icon: typeof Globe }> = [
    { v: 'PUBLIC', label: t('feedback:publish.public'), hint: t('feedback:publish.publicHint'), icon: Globe },
    { v: 'TARGETED', label: t('feedback:publish.targeted'), hint: t('feedback:publish.targetedHint'), icon: Lock },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-label={t('feedback:publish.title')}
      onClick={close}>
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('feedback:publish.title')}</h2>
          <button type="button" onClick={close} disabled={submitting}
            aria-label={t('feedback:publish.close')}
            className="touch-manipulation rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
            <X size={15} />
          </button>
        </div>

        <div className="space-y-3.5 overflow-y-auto px-4 py-3.5">
          <label className="block">
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">
              {t('feedback:publish.titleLabel', { min: ANNOUNCEMENT_TITLE_MIN, max: TITLE_MAX })}
            </span>
            <input className={INPUT_BASE} value={title} maxLength={TITLE_MAX} disabled={submitting}
              placeholder={t('feedback:publish.titlePlaceholder')}
              onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label className="block">
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">
              {t('feedback:publish.descriptionLabel', { min: ANNOUNCEMENT_DESCRIPTION_MIN })}
            </span>
            <textarea className={`${INPUT_BASE} min-h-28 resize-y`} value={description}
              maxLength={BODY_MAX} disabled={submitting}
              placeholder={t('feedback:publish.descriptionPlaceholder')}
              onChange={(e) => setDescription(e.target.value)} />
            <span className="mt-0.5 block text-right text-chip text-slate-400">{description.length}/{BODY_MAX}</span>
          </label>

          <div role="radiogroup" aria-label={t('feedback:publish.visibilityLabel')}>
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:publish.visibilityLabel')}</span>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {choixVisibilite.map(({ v, label, hint, icon: Icon }) => (
                <button key={v} type="button" role="radio" aria-checked={visibility === v}
                  onClick={() => setVisibility(v)} disabled={submitting}
                  className={[
                    'touch-manipulation flex items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
                    visibility === v
                      ? 'bg-slate-100 ring-1 ring-inset ring-slate-300 dark:bg-slate-800 dark:ring-slate-600'
                      : 'bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-800',
                  ].join(' ')}>
                  <Icon size={14} className="mt-0.5 shrink-0 text-slate-500" aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-meta font-semibold text-slate-700 dark:text-slate-200">{label}</span>
                    <span className="block text-chip text-slate-500 dark:text-slate-400">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {ciblee && (
            <div>
              <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">
                {t('feedback:publish.inviteesLabel')}
              </span>
              {invites.length > 0 && (
                <ul className="mb-1.5 flex flex-wrap gap-1" aria-label={t('feedback:publish.inviteesSelected', { count: invites.length })}>
                  {invites.map((p) => (
                    <li key={p.id} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-chip text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {p.name}
                      <button type="button" onClick={() => toggleInvite(p)} disabled={submitting}
                        aria-label={t('feedback:publish.removeInvitee', { name: p.name })}
                        className="touch-manipulation rounded-sm text-slate-400 hover:text-slate-600">
                        <X size={11} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="relative">
                <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
                <input type="search" className={`${INPUT_BASE} pl-7`} value={saisie} disabled={submitting}
                  placeholder={t('feedback:publish.searchPlaceholder')}
                  aria-label={t('feedback:publish.searchPlaceholder')}
                  onChange={(e) => setSaisie(e.target.value)} />
              </div>
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-100 dark:border-slate-800">
                {invitables.isLoading ? (
                  <p className="px-2.5 py-2 text-meta text-slate-400" aria-busy="true">{t('feedback:publish.searching')}</p>
                ) : proposees.length === 0 ? (
                  <p className="px-2.5 py-2 text-meta text-slate-500 dark:text-slate-400" role="status">{t('feedback:publish.noUser')}</p>
                ) : (
                  <ul>
                    {proposees.map((p) => {
                      const choisie = invites.some((x) => x.id === p.id);
                      return (
                        <li key={p.id}>
                          <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-meta text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800">
                            <input type="checkbox" checked={choisie} disabled={submitting}
                              onChange={() => toggleInvite(p)} className="accent-slate-700" />
                            <span className="min-w-0 flex-1 truncate">{p.name}</span>
                            {choisie && <Check size={12} className="shrink-0 text-slate-500" aria-hidden />}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div>
            <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">
              {t('feedback:publish.attachments', { max: MAX_ATTACHMENTS })}
            </span>
            {files.length > 0 && (
              <ul className="mb-1.5 space-y-1">
                {files.map((f, idx) => (
                  <li key={`${f.name}-${idx}`}
                    className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-1.5 text-meta text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    <span className="truncate">{f.name} · {Math.ceil(f.size / 1024)} Ko</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))} disabled={submitting}
                      aria-label={t('feedback:publish.removeAttachment', { name: f.name })}
                      className="touch-manipulation ml-2 shrink-0 rounded-sm p-0.5 text-slate-400 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf"
              className="hidden" onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ''; }} />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= MAX_ATTACHMENTS || submitting}
              className={`touch-manipulation ${BTN_SECONDARY} inline-flex items-center gap-1.5 disabled:opacity-45`}>
              <Paperclip size={13} /> {t('feedback:publish.addAttachment')}
            </button>
          </div>

          {error && (
            <p className="rounded-md bg-red-50 px-2.5 py-1.5 text-meta text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-800">
          <button type="button" className={`touch-manipulation ${BTN_SECONDARY} disabled:opacity-45`} onClick={close} disabled={submitting}>
            {t('feedback:publish.cancel')}
          </button>
          <button type="button" className={`touch-manipulation ${BTN_PRIMARY} disabled:opacity-45`} onClick={() => void submit()}
            disabled={!canSubmit}>
            {submitting ? t('feedback:publish.submitting') : t('feedback:publish.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
